import { useState, useEffect, useMemo, Fragment } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Moment from "react-moment";
import { NavLink } from "react-router-dom";
import { Paper, TableHead, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TableSortLabel, Checkbox, ListItemText, OutlinedInput, Menu, TextField, Box } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { CSVLink } from "react-csv";
import FleetWebApi from "../Api/FleetWebApi";
import { useAuthDataContext } from "../AuthDataProvider";

const DEFAULT_STATUS_OPTIONS = [
  "In Review",
  "Review Later",
  "Incorrect FW",
  "Checked, Good",
  "Checked, Bad",
  "Old Algo",
  "Offline",
  "Reached Out To Cx",
];

// Label used in column filters for cells with no value (shown as "null" in the table)
const EMPTY_FILTER_VALUE = "(empty)";

function Reports(props) {
  const [data, setData] = useState([]);
  const [fullData, setFullData] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [title, setTitle] = useState("");
  const [reportTimestamp, setReportTimestamp] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [headers, setHeaders] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [start, setStart] = useState(0);
  const [finish, setFinish] = useState(100);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState("");
  const [popupTitle, setPopupTitle] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [draggedColumn, setDraggedColumn] = useState(null);
  // Column filters (opt-in via props.enableColumnFilters): { heading: [selected values] }.
  // Kept when props.data changes (e.g. a Repeat Offenders row moving to Ignored).
  const [columnFilters, setColumnFilters] = useState({});
  const [filterMenu, setFilterMenu] = useState(null); // { heading, anchorEl }
  const [filterSearch, setFilterSearch] = useState("");
  const { user } = useAuthDataContext();
  const statusTag = props.statusTag || props.title;
  const statusOptions = props.statusOptions || DEFAULT_STATUS_OPTIONS;
  const isStatusControlled = props.statuses !== undefined;
  const currentStatuses = isStatusControlled ? props.statuses : statuses;
  const handleChange = (event, coreID, index) => {
    async function addTaggedNote() {
      // statusTag is the tag, event is the note
      await FleetWebApi.addNote(
        coreID,
        statusTag + ": " + event.target.value,
        "Device",
        user?.user_details?.name || user?.user_details?.email || "Unknown FleetWeb user"
      ).then(
        (result) => {
          console.log(result);
        },
        (error) => {
          console.log(error);
        }
      );
    }
    if (isStatusControlled) {
      props.onStatusChange?.(coreID, event.target.value);
    } else {
      statuses[coreID] = event.target.value;
      setData(filteredData.slice(start, finish));
    }
    addTaggedNote();
  };
  const nextPage = () => {
    setStart(start + 100);
    setFinish(finish + 100);
  };

  const previousPage = () => {
    setStart(start - 100);
    setFinish(finish - 100);
  };

  const handleCellClick = (content, heading) => {
    if (content) {
      setPopupTitle(heading);
      setPopupContent(content.toString());
      setPopupOpen(true);
    }
  };

  const handlePopupClose = () => {
    setPopupOpen(false);
    setPopupContent("");
    setPopupTitle("");
  };

  const handleSort = (field) => {
    const isAsc = sortField === field && sortDirection === "asc";
    const newDirection = isAsc ? "desc" : "asc";
    setSortField(field);
    setSortDirection(newDirection);

    const sortedData = [...fullData].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];

      // Handle null/undefined values
      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";

      // Convert to strings for comparison
      aVal = aVal.toString().toLowerCase();
      bVal = bVal.toString().toLowerCase();

      // Check if values are numbers
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);

      const isoDate = /^\d{4}-\d{2}-\d{2}/;
      const isNumeric =
        !isNaN(aNum) && !isNaN(bNum) && !(isoDate.test(aVal) && isoDate.test(bVal));

      if (isNumeric) {
        return newDirection === "asc" ? aNum - bNum : bNum - aNum;
      } else {
        if (newDirection === "asc") {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      }
    });

    setFullData(sortedData);
    // Jump back to the first page so the top of the newly-sorted results
    // is what's visible, rather than staying on whatever page offset was
    // scrolled to before sorting.
    setStart(0);
    setFinish(100);
  };

  const handleDragStart = (e, columnIndex) => {
    setDraggedColumn(columnIndex);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;

    const newHeaders = [...headers];
    const draggedItem = newHeaders[draggedColumn];
    newHeaders.splice(draggedColumn, 1);
    newHeaders.splice(dropIndex, 0, draggedItem);

    setHeaders(newHeaders);
    setDraggedColumn(null);
  };

  // props.leadingColumns (optional) are shown first, in that order; the remaining columns
  // keep their original order. Leading columns missing from the data are skipped.
  const getOrderedHeaders = (dataItem) => {
    const keys = Object.keys(dataItem);
    const leading = (props.leadingColumns || []).filter((key) => keys.includes(key));
    return [...leading, ...keys.filter((key) => !leading.includes(key))];
  };

  const visibleHeaders = headers.filter(
    (heading) => !hiddenColumns.includes(heading)
  );

  // The regular event-spammers-report table shouldn't offer column
  // hiding - its columns are fixed and meaningful as a whole.
  const showColumnPicker = title !== "event-spammers-report";

  // --- Column filters (props.enableColumnFilters) ---------------------------------------
  // Clicking a column name opens a checklist of that column's values; rows are shown if they
  // match at least one ticked value in every filtered column. props.multiValueColumns maps a
  // column to the separator for cells holding several values (e.g. { Events: ", " }), so each
  // value can be filtered on individually.
  const filtersEnabled = !!props.enableColumnFilters;
  const multiValueColumns = props.multiValueColumns || {};

  const cellFilterValues = (item, heading) => {
    const value = item[heading];
    if (value === null || value === undefined || value === "") return [EMPTY_FILTER_VALUE];
    const separator = multiValueColumns[heading];
    if (!separator) return [String(value)];
    const parts = String(value).split(separator).map((part) => part.trim()).filter(Boolean);
    return parts.length ? parts : [EMPTY_FILTER_VALUE];
  };

  const activeFilterCount = Object.values(columnFilters).filter((values) => values.length).length;

  const filteredData = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, values]) => values.length);
    if (!filtersEnabled || active.length === 0) return fullData;
    return fullData.filter((item) =>
      active.every(([heading, values]) =>
        cellFilterValues(item, heading).some((value) => values.includes(value))
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullData, columnFilters, filtersEnabled]);

  // Values (with row counts) offered in the open filter menu; ticked values stay listed
  // even if no current row has them, so they can still be unticked
  const filterOptions = useMemo(() => {
    if (!filterMenu) return [];
    const counts = new Map();
    fullData.forEach((item) => {
      cellFilterValues(item, filterMenu.heading).forEach((value) => {
        counts.set(value, (counts.get(value) || 0) + 1);
      });
    });
    (columnFilters[filterMenu.heading] || []).forEach((value) => {
      if (!counts.has(value)) counts.set(value, 0);
    });
    return [...counts.entries()].sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMenu, fullData, columnFilters]);

  const visibleFilterOptions = filterOptions.filter(([value]) =>
    value.toLowerCase().includes(filterSearch.toLowerCase())
  );

  const setFilterValues = (heading, values) => {
    setColumnFilters((prev) => ({ ...prev, [heading]: values }));
    setStart(0);
    setFinish(100);
  };

  const toggleFilterValue = (heading, value) => {
    const current = columnFilters[heading] || [];
    setFilterValues(
      heading,
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    );
  };

  const openFilterMenu = (event, heading) => {
    setFilterSearch("");
    setFilterMenu({ heading, anchorEl: event.currentTarget });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
    setStart(0);
    setFinish(100);
  };

  const handleColumnVisibilityChange = (event) => {
    const selected = event.target.value;
    setHiddenColumns(headers.filter((heading) => !selected.includes(heading)));
  };

  // Rebuilds the full processed dataset whenever the underlying report data
  // actually changes (new report loaded) - NOT on pagination, so paging
  // through the report doesn't re-derive from props.data and silently
  // discard whatever sort was applied via handleSort.
  useEffect(() => {
    if (props.data) {
      setIsLoading(true);
      setTitle(props.title);

      // % Threshold column
      let processedData = props.data;
      if (props.title === "event-spammers-report") {
        // Event Thresholds Map
        const eventThresholds = {
          "/V1/Flood": 24,
          "/V1/Puck": 60,
          "/V2/Device/Puck": 60,
          "/V2/Device/Shuttie2": 50,
          "/V2/Device/FlowieO": 100,
          "/V2/Device/WaterMeter": 100,
          "/V2/Device/Hubbie": 50,
          "/V1/Outage": 24,
          "/V1/STSensor": 500,
          "AL/Sentree/Inrush": 200,
          "AL/Dbg/Lockup": 2,
          "AL/Dbg/Adopt": 100,
          "AL/Dbg/Recycle": 5,
          "AL/STSensor": 250,
          "PANIC": 1,
          "WATCHDOG": 1,
          "USER": 1,
          "AL/Dbg": 100,
          "AL/Dbg/Lora": 30,
          "crpt": 100,
          "AL/Dbg/Publish/Discard": 1,
          "waveform": 10,
          "AL/Dbg/Sweep": 50,
          "AL/Dbg/Lora/Spam": 50,
          "reboot": 5
        };

        processedData = props.data.map(item => {
          const lastDayCount = parseFloat(item["LastDayCount"] || 0);
          const eventType = item["event"] || "";
          const threshold = eventThresholds[eventType];

          let thresholdValue = "N/A";
          if (threshold !== undefined) {
            const percentage = Math.round((lastDayCount / threshold) * 100);
            thresholdValue = percentage;
          }

          return {
            ...item,
            "% Threshold": thresholdValue
          };
        });
      }

      setFullData(processedData);
      setReportTimestamp(props.timestamp);
      setHeaders(getOrderedHeaders(processedData[0]));
      setHiddenColumns(props.defaultHiddenColumns || []);
      setSortField("");
      setSortDirection("asc");
      setStart(0);
      setFinish(100);
    }
  }, [props.data, props.title, props.timestamp]);

  // Keeps the visible page in sync with the full (possibly sorted, possibly filtered)
  // dataset and the current pagination window.
  useEffect(() => {
    setData(filteredData.slice(start, finish));
    setIsLoading(false);
  }, [filteredData, start, finish]);

  useEffect(() => {
    let isMounted = true;
    async function getDeviceStatuses() {
      await FleetWebApi.checkForTaggedNote(
        props.data.map((item) => item.coreID),
        statusTag
      ).then(
        (result) => {
          setStatuses(result);
        },
        (error) => {
          console.log(error.message);
        }
      );

      setIsStatusLoading(false);
    }
    if (props.showStatusField && !isStatusControlled) {
      setIsStatusLoading(true);
      getDeviceStatuses();
    } else if (isStatusControlled) {
      setIsStatusLoading(false);
    }
    if (props.data.length !== 0) {
      if (isMounted) {
        setIsLoading(false);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [props.data]);

  return (
    <Fragment>
      <Grid
        container
        spacing={8}
        // justifyContent="space-between"
        // alignItems="flex-start"
      >
        <Grid item xs={4} md={4} lg={4}>
          <h2>{title}</h2>
        </Grid>
        <Grid
          item
          xs={showColumnPicker ? 8 : 4}
          md={showColumnPicker ? 8 : 4}
          lg={showColumnPicker ? 8 : 4}
          sx={props.alignTimestampRight ? { textAlign: "right", pr: 2 } : undefined}
        >
          {isLoading === 0 && <CircularProgress color="secondary" />}
          {!isLoading && reportTimestamp && (
            <h3>
              <Moment format="YYYY-MM-DD HH:mm:ss Z">{reportTimestamp}</Moment>
            </h3>
          )}
        </Grid>

        {!isLoading && !showColumnPicker && (
          <Grid item xs={4} md={4} lg={4}>
            <h3>
              <CSVLink data={activeFilterCount > 0 ? filteredData : props.data} filename={title + "_.csv"}>
                Download CSV
              </CSVLink>
            </h3>
          </Grid>
        )}
      </Grid>
      {props.headerContent && <div style={{ padding: "0 16px" }}>{props.headerContent}</div>}
      {!isLoading && showColumnPicker && (
        <Grid container sx={{ px: 2 }}>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <h3 style={{ margin: 0 }}>
              <CSVLink data={activeFilterCount > 0 ? filteredData : props.data} filename={title + "_.csv"}>
                Download CSV
              </CSVLink>
            </h3>
          </Grid>
        </Grid>
      )}
      {!isLoading && showColumnPicker && headers.length > 0 && (
        <Grid container sx={{ px: 2, mt: 1 }}>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="column-visibility-label">Columns</InputLabel>
              <Select
                labelId="column-visibility-label"
                multiple
                value={visibleHeaders}
                onChange={handleColumnVisibilityChange}
                input={<OutlinedInput label="Columns" />}
                renderValue={(selected) =>
                  `${selected.length} / ${headers.length} columns shown`
                }
              >
                {headers.map((heading) => (
                  <MenuItem key={heading} value={heading}>
                    <Checkbox checked={visibleHeaders.includes(heading)} />
                    <ListItemText primary={heading} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
        <Grid item xs={12}>
          {!isLoading && (
            <Paper sx={{ 
              height: title === "sad-water-devices" ? "calc(100vh - 300px)" : "70vh", 
              minHeight: "500px",
              display: "flex", 
              flexDirection: "column" 
            }}>
              <TableContainer sx={{ 
                flexGrow: 1,
                overflow: "auto",
                ...(title === "sad-water-devices" && {
                  '&::-webkit-scrollbar': {
                    height: '12px',
                    width: '12px'
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '6px'
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: '#555'
                  }
                })
              }}>
                <Table size="small" stickyHeader aria-label="a dense table">
                  <TableHead>
                    <TableRow>
                      {visibleHeaders.map((heading) => (
                        <TableCell
                          key={heading}
                          draggable
                          onDragStart={(e) => handleDragStart(e, headers.indexOf(heading))}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, headers.indexOf(heading))}
                          sx={{ 
                            fontWeight: 'bold',
                            backgroundColor: '#353535ff',
                            minWidth: heading === 'coreID' ? '150px' : '120px',
                            whiteSpace: 'nowrap',
                            cursor: 'move',
                            userSelect: 'none',
                            '&:hover': {
                              backgroundColor: '#404040ff'
                            }
                          }}
                        >
                          {filtersEnabled ? (
                            // Column name opens the filter menu; the arrow beside it sorts
                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              <Box
                                component="span"
                                onClick={(e) => openFilterMenu(e, heading)}
                                title={"Filter by " + heading}
                                sx={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  color: '#fff',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {heading}
                                {columnFilters[heading]?.length > 0 && (
                                  <FilterAltIcon sx={{ fontSize: 16, ml: 0.5, color: '#64b5f6' }} />
                                )}
                              </Box>
                              <TableSortLabel
                                active={sortField === heading}
                                direction={sortField === heading ? sortDirection : 'asc'}
                                onClick={() => handleSort(heading)}
                                title={"Sort by " + heading}
                                sx={{
                                  ml: 0.5,
                                  color: '#fff !important',
                                  '& .MuiTableSortLabel-icon': {
                                    color: '#fff !important',
                                    // Always show the arrow (faint until active) so sorting is discoverable
                                    opacity: sortField === heading ? 1 : '0.5 !important'
                                  }
                                }}
                              />
                            </Box>
                          ) : (
                            <TableSortLabel
                              active={sortField === heading}
                              direction={sortField === heading ? sortDirection : 'asc'}
                              onClick={() => handleSort(heading)}
                              sx={{
                                color: '#fff !important',
                                '& .MuiTableSortLabel-icon': {
                                  color: '#fff !important'
                                }
                              }}
                            >
                              {heading}
                            </TableSortLabel>
                          )}
                        </TableCell>
                      ))}
                      {props.showStatusField ? (
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          backgroundColor: '#353535ff',
                          minWidth: '180px'
                        }}>
                          Status
                        </TableCell>
                      ) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((item, index) => (
                      <TableRow
                        key={index + "row"}
                        sx={
                          props.statusColors?.[currentStatuses[item.coreID]]
                            ? {
                                "& td, & td a": {
                                  color: props.statusColors[currentStatuses[item.coreID]],
                                },
                              }
                            : undefined
                        }
                      >
                        {visibleHeaders.map((heading, ind) => {
                          if (heading === "coreID") {
                            return (
                              <TableCell key={index + "cell" + ind}>
                                <NavLink
                                  to={"/device/" + item[heading]}
                                  className="Nav_link"
                                  key={index + "navlink" + ind}
                                >
                                  {item[heading]}
                                </NavLink>
                              </TableCell>
                            );
                          } else if (heading === "status") {
                            return null;
                          }
                          return (
                            <TableCell 
                              key={ind}
                              sx={{ 
                                whiteSpace: 'nowrap',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#353535ff'
                                }
                              }}
                              title="Click to view full content"
                              onClick={() => handleCellClick(item[heading], heading)}
                            >
                              {item[heading] ||
                              item[heading] === false ||
                              item[heading] === 0
                                ? item[heading].toString()
                                : "null"}
                            </TableCell>
                          );
                        })}
                        {props.showStatusField && !isStatusLoading ? (
                          <TableCell>
                            <FormControl
                              sx={{ m: 1, minWidth: 120 }}
                              size="small"
                            >
                              <InputLabel id="demo-select-small">
                                Status
                              </InputLabel>
                              <Select
                                labelId="demo-select-small"
                                id="demo-select-small"
                                value={
                                  statusOptions.includes(currentStatuses[item.coreID])
                                    ? currentStatuses[item.coreID]
                                    : "Unchecked"
                                }
                                label="Status"
                                onChange={(event) =>
                                  handleChange(event, item.coreID, index)
                                }
                              >
                                <MenuItem value={"Unchecked"}>
                                  Unchecked
                                </MenuItem>
                                {statusOptions.map((option) => (
                                  <MenuItem value={option} key={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        ) : props.showStatusField ? (
                          <CircularProgress color="secondary" />
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination and loading controls */}
              <div style={{ 
                padding: '16px', 
                borderTop: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#353535ff'
              }}>
                <div>
                  {isLoading && <CircularProgress color="secondary" size={24} />}
                  {!isLoading && (
                    <span style={{ fontSize: '14px', color: '#fff' }}>
                      {filteredData.length === 0
                        ? "No records match the filters"
                        : `Showing ${start + 1} - ${Math.min(start + 100, filteredData.length)} of ${filteredData.length} records`}
                      {activeFilterCount > 0 && ` (filtered from ${fullData.length})`}
                    </span>
                  )}
                  {!isLoading && activeFilterCount > 0 && (
                    <Button size="small" onClick={clearAllFilters} sx={{ ml: 2 }}>
                      Clear all filters
                    </Button>
                  )}
                </div>
                <div>
                  {start !== 0 && (
                    <IconButton onClick={previousPage} title="Previous page">
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  {start + 100 < filteredData.length && (
                    <IconButton onClick={nextPage} title="Next page">
                      <ArrowForwardIcon />
                    </IconButton>
                  )}
                </div>
              </div>
            </Paper>
          )}
        </Grid>
      </Grid>
      
      {/* Column filter menu: tick one or more values of the clicked column */}
      <Menu
        anchorEl={filterMenu?.anchorEl}
        open={!!filterMenu}
        onClose={() => setFilterMenu(null)}
        disableAutoFocusItem
        PaperProps={{ sx: { maxHeight: 480, minWidth: 260 } }}
      >
        {filterMenu && (
          <Box sx={{ px: 2, pt: 1, pb: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Filter by {filterMenu.heading}
            </Typography>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Search values"
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              // Keep the menu's type-to-select from stealing keystrokes
              onKeyDown={(e) => e.stopPropagation()}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Button
                size="small"
                onClick={() =>
                  setFilterValues(filterMenu.heading, [
                    ...new Set([
                      ...(columnFilters[filterMenu.heading] || []),
                      ...visibleFilterOptions.map(([value]) => value),
                    ]),
                  ])
                }
              >
                Select all
              </Button>
              <Button size="small" onClick={() => setFilterValues(filterMenu.heading, [])}>
                Clear
              </Button>
            </Box>
          </Box>
        )}
        {filterMenu &&
          visibleFilterOptions.map(([value, count]) => (
            <MenuItem
              key={value}
              dense
              onClick={() => toggleFilterValue(filterMenu.heading, value)}
            >
              <Checkbox
                size="small"
                checked={(columnFilters[filterMenu.heading] || []).includes(value)}
              />
              <ListItemText primary={value} secondary={`${count} row${count === 1 ? "" : "s"}`} />
            </MenuItem>
          ))}
        {filterMenu && visibleFilterOptions.length === 0 && (
          <MenuItem disabled dense>
            No matching values
          </MenuItem>
        )}
      </Menu>

      {/* Popup Dialog for Full Content */}
      <Dialog 
        open={popupOpen} 
        onClose={handlePopupClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {popupTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {popupContent}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePopupClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

export default Reports;
